/**
 * Party Ledgers Page
 *
 * Single entry point for viewing customer and supplier ledgers - pick a party
 * from the dropdown, or deep-link with ?customerId=/?supplierId= to land here
 * with a specific party pre-selected. The actual ledger (table, filters,
 * summary, export, payment actions) is rendered by the shared <LedgerView>,
 * the same component used regardless of how the party was selected.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import PageHeader from '../components/ui/PageHeader';
import LedgerView from '../components/ledger/LedgerView';
import { CustomerService } from '../services/customerService';
import { PurchaseService } from '../services/purchaseService';
import { useSnackbar } from 'notistack';

interface Party {
  id: string;
  name: string;
  type: 'customer' | 'supplier';
  balance: number;
}

const PartyLedgersPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();

  const [partyType, setPartyType] = useState<'customer' | 'supplier'>('customer');
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(false);
  const [deepLinkResolving, setDeepLinkResolving] = useState(false);

  const fetchParties = async (type: 'customer' | 'supplier') => {
    setLoading(true);
    try {
      const endpoint = type === 'customer'
        ? `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/customers`
        : `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/suppliers`;

      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Failed to fetch parties');
      const data = await response.json();
      const items = data.items || (Array.isArray(data) ? data : []);
      const formattedParties: Party[] = items.map((item: any) => ({
        id: item.id,
        name: type === 'customer' ? item.name : item.business_name,
        type,
        balance: item.outstanding_balance || 0,
      }));
      setParties(formattedParties);
    } catch (error) {
      console.error('Error fetching parties:', error);
      enqueueSnackbar('Failed to load parties', { variant: 'error' });
      setParties([]);
    } finally {
      setLoading(false);
    }
  };

  // Resolve a deep-linked ?customerId=/?supplierId= on first mount, independent
  // of whatever page of the party list happens to be loaded.
  useEffect(() => {
    const customerId = searchParams.get('customerId');
    const supplierId = searchParams.get('supplierId');
    if (!customerId && !supplierId) return;

    const resolve = async () => {
      setDeepLinkResolving(true);
      try {
        if (customerId) {
          setPartyType('customer');
          const customer = await CustomerService.getCustomer(customerId);
          setSelectedParty({
            id: customer.id,
            name: customer.name,
            type: 'customer',
            balance: (customer as any).outstanding_balance || 0,
          });
        } else if (supplierId) {
          setPartyType('supplier');
          const supplier = await PurchaseService.getSupplier(supplierId);
          setSelectedParty({
            id: supplier.id,
            name: supplier.businessName,
            type: 'supplier',
            balance: supplier.outstandingAmount || 0,
          });
        }
      } catch (error) {
        console.error('Error resolving deep-linked party:', error);
        enqueueSnackbar('Failed to load the requested party', { variant: 'error' });
      } finally {
        setDeepLinkResolving(false);
      }
    };
    resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchParties(partyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyType]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  return (
    <Box>
      <PageHeader
        title="Party Ledgers"
        subtitle="View financial ledgers for customers and suppliers"
      />

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Party Type
            </Typography>
            <ToggleButtonGroup
              value={partyType}
              exclusive
              onChange={(_, newType) => {
                if (newType) {
                  setPartyType(newType);
                  setSelectedParty(null);
                }
              }}
              aria-label="Party type selection"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="customer" aria-label="Customer">
                <PeopleIcon sx={{ mr: 1 }} />
                Customers
              </ToggleButton>
              <ToggleButton value="supplier" aria-label="Supplier">
                <BusinessIcon sx={{ mr: 1 }} />
                Suppliers
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Autocomplete
              options={parties}
              getOptionLabel={(option) => option.name}
              value={selectedParty}
              onChange={(_, newValue) => setSelectedParty(newValue)}
              loading={loading || deepLinkResolving}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${partyType === 'customer' ? 'Customer' : 'Supplier'}`}
                  placeholder="Search by name..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(loading || deepLinkResolving) ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{option.name}</Typography>
                      <Typography variant="body2" sx={{ color: option.balance >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                        {formatCurrency(option.balance)}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              sx={{ width: '100%' }}
            />
          </Box>
        </Stack>

        {selectedParty && (
          <Box sx={{ mt: 4 }}>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>{selectedParty.name}</Typography>
                    <Chip
                      label={selectedParty.type === 'customer' ? 'Customer' : 'Supplier'}
                      size="small"
                      color={selectedParty.type === 'customer' ? 'primary' : 'secondary'}
                      sx={{ borderRadius: 4 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <LedgerView partyType={selectedParty.type} partyId={selectedParty.id} partyName={selectedParty.name} />
          </Box>
        )}

        {!selectedParty && !deepLinkResolving && (
          <Box sx={{ mt: 4, p: 6, textAlign: 'center', backgroundColor: 'rgba(33, 150, 243, 0.04)', borderRadius: 2 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Select a Party to View Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a customer or supplier from the dropdown above to view their financial transactions
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PartyLedgersPage;
